import { Component, OnInit } from '@angular/core';
import {
  ChartMetadata,
  DeleteChartOrDashboardInput,
  DeleteChartsOrDashboardsMutation,
  DeleteChartsOrDashboardsMutationVariables,
  DeleteResponse,
  DashboardNameInfo,
  Engagements,
  EntityType,
  GetChartsQuery,
  GetChartsQueryVariables,
  GetDashboardNamesQuery,
  GetDashboardNamesQueryVariables,
} from '../../../../API';
import { AppService } from '../../../shared/services/app.service';
import { ApiService } from '../../../shared/services/api.service';
import { NzMessageService } from 'ng-zorro-antd/message';
import { GetCharts, GetDashboardNames } from '../../../../graphql/queries';
import { DeleteChartsOrDashboards } from '../../../../graphql/mutations';
import { NzModalService } from 'ng-zorro-antd/modal';
import { Router } from '@angular/router';
import { S3StorageService } from '../../../shared/services/s3-storage.service';

@Component({
  selector: 'app-manage-charts',
  templateUrl: './manage-charts.component.html',
  styleUrl: './manage-charts.component.scss',
})
export class ManageChartsComponent implements OnInit {
  engagement!: string;
  charts: ChartMetadata[] = [];
  filteredCharts: ChartMetadata[] = [];
  isLoading: boolean = false;
  sortDescending: boolean = true;
  searchTerm: string = '';

  currentPage: number = 1;
  pageSize: number = 4;
  total: number = 0;
  spinnerContent: string = 'Loading Charts...';

  constructor(
    private appService: AppService,
    private apiService: ApiService,
    private nzMessageService: NzMessageService,
    private modalService: NzModalService,
    private router: Router,
    private s3Service: S3StorageService,
  ) {}

  ngOnInit(): void {
    this.appService.activeEngagement$.subscribe((engagement: Engagements) => {
      this.engagement = engagement.engagement;
      this.loadCharts();
    });
  }

  private loadCharts(): void {
    if (!this.engagement) return;

    this.isLoading = true;
    this.spinnerContent = 'Loading Charts...';
    this.apiService
      .query<GetChartsQuery, GetChartsQueryVariables>(GetCharts, {
        engagementId: this.engagement,
      })
      .then((response) => {
        this.charts = (response.data?.GetCharts as ChartMetadata[]) || [];
        this.applyFiltersAndSort();
      })
      .catch((error) => {
        this.nzMessageService.error(`Failed to load charts: ${error}`);
        this.charts = [];
      })
      .finally(() => {
        this.isLoading = false;
      });
  }

  private sortCharts(): void {
    this.filteredCharts.sort((a, b) => {
      const dateA = new Date(a.lastModifiedAt || 0);
      const dateB = new Date(b.lastModifiedAt || 0);

      if (this.sortDescending) {
        return dateB.getTime() - dateA.getTime();
      } else {
        return dateA.getTime() - dateB.getTime();
      }
    });
  }

  private filterCharts(): void {
    if (!this.searchTerm.trim()) {
      this.filteredCharts = [...this.charts];
    } else {
      const searchLower = this.searchTerm.toLowerCase().trim();
      this.filteredCharts = this.charts.filter((chart) => {
        const chartName = chart.name?.toLowerCase() || '';
        const chatType = chart.chartType?.toLowerCase() || '';
        const lastModifiedBy = chart.lastModifiedBy?.toLowerCase() || '';
        const createdBy = chart.createdBy?.toLowerCase() || '';

        return (
          chartName.includes(searchLower) ||
          chatType.includes(searchLower) ||
          lastModifiedBy.includes(searchLower) ||
          createdBy.includes(searchLower)
        );
      });
    }
  }

  private applyFiltersAndSort(): void {
    this.filterCharts();
    this.sortCharts();
    this.total = this.filteredCharts.length;
  }

  onPageChange(page: number): void {
    this.currentPage = page;
  }

  onPageSizeChange(size: number): void {
    this.pageSize = size;
    this.currentPage = 1;
  }

  onSearchChange(searchTerm: string): void {
    this.searchTerm = searchTerm;
    this.currentPage = 1;
    this.applyFiltersAndSort();
  }

  toggleSortDirection(): void {
    this.sortDescending = !this.sortDescending;
    this.sortCharts();
  }

  onMenuAction(event: any): void {
    const { action, data: chart } = event;
    switch (action) {
      case 'view':
        this.router.navigate(['/bi/view-chart', chart.chartId]);
        break;
      case 'edit':
        this.router.navigate(['/bi/edit-chart', chart.chartId]);
        break;
      case 'export': {
        const imageUrl = `${this.appService.domainUrl}/images/chart/${chart.engagement}/${chart.chartId}/chart.svg`;
        this.s3Service.download(imageUrl, `${chart.name}.svg`);
        break;
      }
      case 'delete':
        this.handleChartDelete(chart);
        break;
    }
  }

  private async handleChartDelete(chart: ChartMetadata): Promise<void> {
    if (chart.dashboardIds && chart.dashboardIds.length > 0) {
      try {
        const response = await this.apiService.query<GetDashboardNamesQuery, GetDashboardNamesQueryVariables>(
          GetDashboardNames,
          {
            engagementId: chart.engagement,
            dashboardIds: chart.dashboardIds as string[],
          },
        );

        const dashboardNames = response.data?.GetDashboardNames as DashboardNameInfo[];
        const dashboardCount = chart.dashboardIds?.length;

        this.modalService.confirm({
          nzTitle: `Cannot Delete Chart`,
          nzContent: `This chart is currently associated with ${dashboardCount} dashboard(s) and cannot be deleted. To proceed with deletion, please remove the chart from all associated dashboards.<br><br><strong>Associated Dashboards:</strong><br>• ${
            dashboardNames
              ?.map((d) => d.name)
              .filter((name) => name)
              .join('<br>• ') || '--'
          }`,
          nzOkText: 'Ok',
          nzCancelText: null,
        });
        return;
      } catch (error) {
        console.error('Error fetching dashboard names:', error);
        this.modalService.confirm({
          nzTitle: `Cannot Delete Chart`,
          nzContent: `This chart is currently associated with ${chart.dashboardIds.length} dashboard(s) and cannot be deleted. To proceed with deletion, please remove the chart from all associated dashboards.`,
          nzOkText: 'Ok',
          nzCancelText: null,
        });
        return;
      }
    }

    this.modalService.confirm({
      nzTitle: `Are you sure you want to delete this chart?`,
      nzContent: 'Deleting this chart will permanently remove it from your workspace. This action cannot be undone.',
      nzOkText: `Yes, delete`,
      nzOnOk: () => this.deleteChart(chart),
      nzCancelText: 'Cancel',
    });
  }

  private deleteChart(chart: ChartMetadata) {
    this.isLoading = true;
    this.spinnerContent = 'Deleting Chart...';
    const deleteInput: DeleteChartOrDashboardInput = {
      engagementId: chart.engagement,
      entityId: chart.chartId,
      entityType: EntityType.CHARTS,
    };
    this.apiService
      .query<DeleteChartsOrDashboardsMutation, DeleteChartsOrDashboardsMutationVariables>(DeleteChartsOrDashboards, {
        input: deleteInput,
      })
      .then((response) => {
        const deleteResponse = response.data?.DeleteChartsOrDashboards as DeleteResponse;
        if (deleteResponse.statusCode !== 200) {
          this.nzMessageService.error(`Failed to delete chart: ${deleteResponse.message}`);
        } else {
          this.nzMessageService.success(`Chart successfully deleted`);
          this.loadCharts();
        }
      })
      .catch((error) => {
        this.nzMessageService.error(`Failed to delete chart: ${error}`);
      })
      .finally(() => {
        this.isLoading = false;
      });
  }

  trackByChartId(index: number, chart: ChartMetadata): string {
    return chart.chartId;
  }
  get paginatedCharts(): ChartMetadata[] {
    const startIndex = (this.currentPage - 1) * this.pageSize;
    const endIndex = startIndex + this.pageSize;
    return this.filteredCharts.slice(startIndex, endIndex);
  }
}
